import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaGavel, FaClock, FaTruck, FaWeightHanging,
  FaMapMarkerAlt, FaMoneyBillWave, FaCalendarAlt,
  FaSpinner, FaInfoCircle, FaCheckCircle, FaTimes,
  FaUsers, FaPlus, FaEdit
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';

function SubbieTenderView({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tender, setTender] = useState(null);
  const [myBid, setMyBid] = useState(null);
  const [user, setUser] = useState({});
  const [subbieId, setSubbieId] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    getSubbieId(userData);
  }, [id]);

  const getSubbieId = async (userData) => {
    try {
      let savedId = localStorage.getItem('subbieId');
      if (savedId) {
        setSubbieId(savedId);
        fetchData(savedId);
        return;
      }

      const response = await fetch(`/subcontractors?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.subcontractors && data.subcontractors.length > 0) {
        const subbieId = data.subcontractors[0].id;
        setSubbieId(subbieId);
        localStorage.setItem('subbieId', subbieId);
        fetchData(subbieId);
      }
    } catch (err) {
      console.error('Error fetching subbie ID:', err);
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  const fetchData = async (subbieId) => {
    setLoading(true);
    try {
      const [tenderRes, bidRes] = await Promise.all([
        fetch(`/tenders/${id}`),
        fetch(`/tenders/${id}/my-bid?subcontractor_id=${subbieId}`)
      ]);

      const tenderData = await tenderRes.json();
      const bidData = await bidRes.json();

      if (tenderData.success) {
        setTender(tenderData.tender);
      }
      if (bidData.success && bidData.bid) {
        setMyBid(bidData.bid);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load tender details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      'open': 'bg-green-100 text-green-600',
      'closed': 'bg-yellow-100 text-yellow-600',
      'awarded': 'bg-blue-100 text-blue-600',
      'cancelled': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="tenders" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading tender details...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  if (!tender) {
    return (
      <SubbieLayout activeTab="tenders" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Tender Not Found</h2>
          <p className="text-gray-500">The tender you're looking for doesn't exist.</p>
          <Link to="/subbie-tenders" className="mt-4 inline-block px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>
            Back to Tender Board
          </Link>
        </div>
      </SubbieLayout>
    );
  }

  const job = tender.jobs;
  const isExpired = new Date(tender.bidding_deadline) < new Date();
  const canBid = tender.status === 'open' && !isExpired;

  return (
    <SubbieLayout activeTab="tenders" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/subbie-tenders" className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: '#0A1929' }}>
          <FaArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            {tender.tender_number}
            <span className={`ml-3 ${getStatusBadge(tender.status)}`}>
              {tender.status}
            </span>
            {tender.tender_type === 'framework' && (
              <span className="ml-2 px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-600">
                Framework
              </span>
            )}
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {job?.commodity || 'Unknown'} • {tender.total_volume_mt || 0} MT
          </p>
        </div>
      </div>

      {/* Tender Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <p className="text-xs text-gray-500">Route</p>
          <p className="font-medium text-navy">{job?.origin} → {job?.destination}</p>
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <p className="text-xs text-gray-500">Deadline</p>
          <p className="font-medium text-navy">{formatDate(tender.bidding_deadline)}</p>
          {isExpired && <p className="text-xs text-red-500">(Expired)</p>}
        </div>
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
          <p className="text-xs text-gray-500">Target Rate</p>
          <p className="font-medium text-navy">R{tender.target_rate || 'N/A'}/MT</p>
        </div>
      </div>

      {/* Tender Details */}
      <div className="bg-white rounded-xl border p-6 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <h2 className="text-lg font-semibold text-navy mb-4">📋 Tender Details</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Commodity</p>
            <p className="font-medium">{job?.commodity || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-500">Total Volume</p>
            <p className="font-medium">{tender.total_volume_mt} MT</p>
          </div>
          <div>
            <p className="text-gray-500">Winners</p>
            <p className="font-medium">{tender.number_of_winners}</p>
          </div>
          <div>
            <p className="text-gray-500">Min Trucks Required</p>
            <p className="font-medium">{tender.min_trucks_required || 'None'}</p>
          </div>
          {tender.tender_type === 'framework' && (
            <>
              <div>
                <p className="text-gray-500">Duration</p>
                <p className="font-medium">{tender.duration_months} months</p>
              </div>
              <div>
                <p className="text-gray-500">Monthly Volume</p>
                <p className="font-medium">{tender.monthly_volume_mt} MT</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Your Bid Status */}
      <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
        <h2 className="text-lg font-semibold text-navy mb-4">📝 Your Bid</h2>
        {myBid ? (
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex flex-wrap justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Your Bid</p>
                <p className="text-2xl font-bold text-green-600">R{myBid.bid_rate}/MT</p>
                <p className="text-sm text-gray-500 mt-1">
                  Trucks: {myBid.trucks_offered} 
                  {myBid.monthly_capacity_mt && ` • Capacity: ${myBid.monthly_capacity_mt} MT/mo`}
                </p>
              </div>
              <div className="flex gap-2">
                {canBid && (
                  <Link
                    to={`/subbie-tenders/${id}/bid`}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <FaEdit size={14} /> Update Bid
                  </Link>
                )}
                <span className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
                  <FaCheckCircle className="inline mr-1" /> Submitted
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500">You haven't submitted a bid for this tender yet.</p>
            {canBid ? (
              <Link
                to={`/subbie-tenders/${id}/bid`}
                className="mt-3 inline-block px-4 py-2 bg-green text-white rounded-lg text-sm hover:bg-green transition-colors flex items-center gap-2"
              >
                <FaPlus size={14} /> Submit Bid
              </Link>
            ) : (
              <p className="text-sm text-red-500 mt-2">
                {isExpired ? 'Bidding deadline has passed.' : 'Tender is no longer open.'}
              </p>
            )}
          </div>
        )}
      </div>
    </SubbieLayout>
  );
}

export default SubbieTenderView;
